import React, { useCallback, useEffect, useMemo } from 'react'
import { useApi } from '~/store/store'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { ServiceResponse } from '@mutual-libs/shared-types'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { DocumentationOnboardContainer } from '../../components'
import { LoanRequestStatus, PendenciesTable } from './components'
import { hasPendenciesInAnalysis, hasRequiredPendenciesAccountStatus } from '~/utils/analysisStatus'
import { ErrorState, LoadingState, RedirectPJAnalysisStep } from '~/components'
import { DocumentAnalysisStatus, DocumentsGroupType, DocumentsGroup } from '@mutual-services/account-documents-types'
import * as R from 'ramda'
import { useHistory } from 'react-router-dom'
import { OnboardingRegisterDataActions, trackEvent } from '~/service/analytics'

const useStyles = makeStyles({
  container: {
    height: '100%',
    display: 'flex'
  }
})

const getRequestStatus = (documents: ServiceResponse<DocumentsGroup[]>): DocumentAnalysisStatus => {
  const statusList = documents.map(document => document.status)

  if (statusList.every(status => status === DocumentAnalysisStatus.PENDING_ANALYSIS)) {
    return DocumentAnalysisStatus.PENDING_ANALYSIS
  }

  if (statusList.every(status => status === DocumentAnalysisStatus.APPROVED)) {
    return DocumentAnalysisStatus.APPROVED
  }

  if (statusList.some(status => status === DocumentAnalysisStatus.PENDING)) {
    return DocumentAnalysisStatus.PENDING
  }

  if (statusList.some(status => status === DocumentAnalysisStatus.PENDING_ANALYSIS)) {
    return DocumentAnalysisStatus.PENDING_ANALYSIS
  }

  return DocumentAnalysisStatus.PENDING_ANALYSIS
}

const getPendenciesPageTitle = (status: DocumentAnalysisStatus) => {
  switch (status) {
    case DocumentAnalysisStatus.APPROVED:
      return 'Suas pendências foram resolvidas'
    case DocumentAnalysisStatus.PENDING_ANALYSIS:
      return 'Suas pendências estão em validação'
    case DocumentAnalysisStatus.PENDING:
      return 'Documentação pendente'
    default:
      return 'Suas pendências estão em validação'
  }
}

const getPendenciesDescription = (status: DocumentAnalysisStatus) => {
  switch (status) {
    case DocumentAnalysisStatus.APPROVED:
      return <Typography>Parabéns, você já resolveu todas as pendências solicitadas e elas já foram validadas pela nossa equipe de crédito. Em breve poderemos confirmar a disponibilidade do seu crédito, por favor aguarde.</Typography>
    case DocumentAnalysisStatus.PENDING_ANALYSIS:
      return <Typography>Parabéns, você já resolveu todas as pendências solicitadas! Os documentos reenviados serão analisados em até <b>dois dias úteis</b>. Lembramos também que novos documentos ainda podem ser solicitados pela nossa equipe, caso necessário.</Typography>
    case DocumentAnalysisStatus.PENDING:
      return <Typography>Analisamos sua documentação e encontramos algumas pendências. Verfique abaixo as pendências encontradas e <b>reenvie os documentos solicitados</b>. Os documentos reenviados serão analisados em até <b>dois dias úteis.</b></Typography>
    default:
      return <Typography>Parabéns, você já resolveu todas as pendências solicitadas! Os documentos reenviados serão analisados em até <b>dois dias úteis</b>. Lembramos também que novos documentos ainda podem ser solicitados pela nossa equipe, caso necessário.</Typography>
  }
}

export const Pendencies: React.FC = () => {
  const { _id: accountId, pjAnalysisStatus } = useAuthAccount()
  const { status } = pjAnalysisStatus
  const [getDocumentsGroupsByAccountId, getDocumentsGroupsByAccountIdState] = useApi('getDocumentsGroupsByAccountId')
  const { error, loading, data: documents } = getDocumentsGroupsByAccountIdState
  const classes = useStyles()
  const shouldRedirect = useMemo(() => !hasRequiredPendenciesAccountStatus(status) && !hasPendenciesInAnalysis(status), [status])
  const history = useHistory()

  const updateAccountDocuments = useCallback(async () => {
    await getDocumentsGroupsByAccountId({ accountId })
  }, [getDocumentsGroupsByAccountId, accountId])

  const fetchDocuments = useCallback(async () => {
    if (shouldRedirect) {
      return
    }

    if (!documents) {
      await updateAccountDocuments()
    }
  }, [documents, updateAccountDocuments])

  useEffect(() => {
    trackEvent(OnboardingRegisterDataActions.PJ_PENDENCIES_PAGE)
    fetchDocuments()
  }, [])

  if (shouldRedirect) {
    return (
      <RedirectPJAnalysisStep
        status={status}
      />
    )
  }

  if (loading || !documents) {
    return <Box className={classes.container}><LoadingState /></Box>
  }

  if (error) {
    return <Box className={classes.container}><ErrorState onRetry={() => getDocumentsGroupsByAccountId({ accountId })} /></Box>
  }

  const filteredDocsWithPendencies = documents.filter(document => document.type === DocumentsGroupType.ADDITIONAL
    ? (document.timeline.some(item => item.status === DocumentAnalysisStatus.PENDING)
      ? (document.status !== DocumentAnalysisStatus.REQUESTED)
      : (document.status === DocumentAnalysisStatus.PENDING || (document.status === DocumentAnalysisStatus.PENDING_ANALYSIS && document.timeline.some(item => item.denialReason !== null))))
    : document.timeline.some(item => item.denialReason !== null))

  const requestStatus = getRequestStatus(filteredDocsWithPendencies)

  if (R.isEmpty(filteredDocsWithPendencies)) {
    history.push({
      pathname: '/app/loan-request/feedback',
      state: { shouldSkipRedirect: true }
    })
  }

  return (
    <DocumentationOnboardContainer
      title={getPendenciesPageTitle(requestStatus)}
      description={getPendenciesDescription(requestStatus)}
    >
      <LoanRequestStatus status={requestStatus} documents={filteredDocsWithPendencies} />
      <PendenciesTable updateDocuments={updateAccountDocuments} documents={filteredDocsWithPendencies} />
    </DocumentationOnboardContainer>
  )
}
